import React, { useRef, useState } from 'react'
import { Trans, WithTranslation } from 'react-i18next'
import { FiLogOut, FiUser } from 'react-icons/fi'
import Flex from 'typescript-styled-flex'
import { ClickAwayListener, Fade, Paper, Popper } from '@material-ui/core'
import Button from '../../../../../components/Template/Button'
import SpinningIcon from '../../../../../components/Template/SpinningIcon'
import { theme } from '../../../../../theme'
import ChangePassword from '../../../../Form/ChangePassword'
import { PremiumWrapper } from '../../../../../components/Template/Premium'
import { IWithSize } from '../../../../BookingCalendar/Calendar/Calendar'
import ButtonLabel from './ButtonLabel'
import Label from './Label'
import UserName, { UserNameHeader } from './UserName'

type AccountOptionsData = IAccountOptionsProps &
  IAccountOptionsTheme &
  WithTranslation &
  IWithSize &
  IAccountOptionsDispatch

function AccountOptions({
  username,
  isAuthorizing,
  t,
  size = 1,
  onReset,
  onLogout,
}: AccountOptionsData) {
  const refMenu = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isChangingPassword, changePassword] = useState(false)

  let canClose = true
  let showTimeout

  function setShouldClose() {
    if (isOpen) {
      canClose = false
    }
  }

  function closeOnClick() {
    clearTimeout(showTimeout)
    setIsOpen(false)
    setTimeout(() => {
      changePassword(false)
    }, 200)
  }

  function togglePopper() {
    if (!canClose) {
      canClose = true
    } else {
      showTimeout = setTimeout(() => setIsOpen(true), 75)
    }
  }

  function closePopper() {
    setTimeout(() => {
      if (!canClose && !isChangingPassword) {
        clearTimeout(showTimeout)
        setIsOpen(false)
      }
    }, 1)
  }

  function toggleToPassword() {
    changePassword(true)
  }

  function shouldClose() {
    setTimeout(() => {
      if (canClose && !isChangingPassword) {
        clearTimeout(showTimeout)
        setIsOpen(false)
      }
    }, 1)
  }

  return (
    <React.Fragment>
      <PremiumWrapper
        size={size}
        ref={refMenu}
        alignCenter={true}
        onMouseEnter={togglePopper}
        onMouseLeave={shouldClose}
        show={isOpen}
      >
        {isAuthorizing && <SpinningIcon color={'white'} />}
        {!isAuthorizing && (
          <React.Fragment>
            <Flex
              justifyCenter={true}
              alignCenter={true}
              style={{
                backgroundColor: theme.colors.palette.kelleyGreen,
                borderRadius: '50%',
                height: `${30 * size}px`,
                pointerEvents: 'none',
                width: `${30 * size}px`,
              }}
            >
              <FiUser size={15 * size} />
            </Flex>
            <UserNameHeader size={size}>
              <span style={{ marginLeft: `${10 * size}px` }}>{username}</span>
            </UserNameHeader>
          </React.Fragment>
        )}
      </PremiumWrapper>
      <Popper
        anchorEl={refMenu.current}
        open={isOpen}
        onMouseLeave={closePopper}
        onMouseEnter={setShouldClose}
        style={{ marginTop: `${-5 * size}px`, zIndex: 50 }}
        popperOptions={{
          positionFixed: true,
        }}
        transition={true}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <ClickAwayListener onClickAway={closeOnClick}>
              <div
                style={{
                  maxWidth: `${345 * size}px`,
                  minWidth: `${345 * size}px`,
                }}
                onMouseEnter={setShouldClose}
              >
                <div
                  style={{
                    borderColor: 'transparent transparent white transparent',
                    borderStyle: 'solid',
                    borderWidth: `0 ${10 * size}px ${10 * size}px ${10 *
                      size}px`,
                    height: '0',
                    marginBottom: '0px',
                    marginTop: `${-10 * size}px`,
                    pointerEvents: 'none',
                    position: 'relative',
                    width: '0',
                  }}
                  x-arrow=""
                />
                <Paper>
                  <Flex column={true} full={true}>
                    <Flex
                      style={{
                        borderBottom: '1px solid #ddd',
                        height: `${50 * size}px`,
                        paddingLeft: `${20 * size}px`,
                      }}
                      alignCenter={true}
                    >
                      <Label size={size}>
                        {isChangingPassword
                          ? t('BBO_936_change_password')
                          : t('BBO_936_my_account')}
                      </Label>
                    </Flex>
                    <Flex
                      row={true}
                      alignCenter={true}
                      style={{
                        padding: isChangingPassword
                          ? `${15 * size}px ${20 * size}px ${20 * size}px ${20 *
                              size}px`
                          : `${20 * size}px`,
                      }}
                    >
                      {isChangingPassword ? (
                        <ChangePassword size={size} />
                      ) : (
                        <React.Fragment>
                          <Flex
                            alignCenter={true}
                            justifyCenter={true}
                            style={{
                              backgroundColor: theme.colors.palette.paleGrey,
                              borderRadius: '50%',
                              height: `${48 * size}px`,
                              marginRight: `${20 * size}px`,
                              width: `${48 * size}px`,
                            }}
                          >
                            <FiUser size={25 * size} />
                          </Flex>
                          <Flex column={true}>
                            <span
                              style={{
                                fontSize: `${14 * size}px`,
                                fontWeight: 'normal',
                                letterSpacing: '0.1px',
                              }}
                            >
                              <Trans i18nKey={'email'}>Login</Trans>
                            </span>
                            <UserName size={size}>{username}</UserName>
                          </Flex>
                        </React.Fragment>
                      )}
                    </Flex>
                    <Flex
                      style={{
                        borderTop: '1px solid #ddd',
                        height: `${60 * size}px`,
                        minHeight: `${60 * size}px`,
                        padding: `0 ${20 * size}px`,
                      }}
                      center={true}
                      full={true}
                    >
                      {isChangingPassword ? (
                        <React.Fragment>
                          <Button
                            size={size}
                            style={{ marginRight: 10, width: '100%' }}
                            onClick={closeOnClick}
                          >
                            <ButtonLabel size={size}>
                              <Trans i18nKey={'cancel'} />
                            </ButtonLabel>
                          </Button>
                          <Button
                            size={size}
                            style={{
                              marginLeft: `${10 * size}px`,
                              width: '100%',
                            }}
                            primary={true}
                            onClick={onReset}
                          >
                            <ButtonLabel size={size}>
                              <Trans i18nKey={'save'} />
                            </ButtonLabel>
                          </Button>
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <Button
                            size={size}
                            style={{
                              marginRight: `${10 * size}px`,
                              width: '100%',
                            }}
                            onClick={toggleToPassword}
                          >
                            <ButtonLabel size={size}>
                              <Trans i18nKey={'BBO_936_change_password'} />
                            </ButtonLabel>
                          </Button>
                          <Button
                            size={size}
                            style={{
                              marginLeft: `${10 * size}px`,
                              width: '100%',
                            }}
                            logout={true}
                            onClick={onLogout}
                          >
                            <FiLogOut size={`${16 * size}px`} color={'white'} />
                            <ButtonLabel size={size}>
                              <Trans i18nKey={'logout'} />
                            </ButtonLabel>
                          </Button>
                        </React.Fragment>
                      )}
                    </Flex>
                  </Flex>
                </Paper>
              </div>
            </ClickAwayListener>
          </Fade>
        )}
      </Popper>
    </React.Fragment>
  )
}

export default AccountOptions

export interface IAccountOptionsTheme {
  className: string
}

export interface IAccountOptionsProps {
  username?: string
  isAuthorizing: boolean
}

export interface IAccountOptionsDispatch {
  onLogout?: () => void
  onReset?: () => void
}
